import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import CloseIcon from "@mui/icons-material/Close";
import { Button, FormHelperText, IconButton, InputAdornment, Popover, TextField } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { DateCalendar } from "@mui/x-date-pickers/DateCalendar";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { PickerDay, type PickerDayProps } from "@mui/x-date-pickers/PickerDay";
import type { ReactNode } from "react";
import { forwardRef, useId, useMemo, useState } from "react";
import { appToday } from "../lib/devDate";

function parseDateValue(value: string) {
  if (!value) return null;
  const [year, month, day] = value.split("-").map(Number);
  if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) return null;
  const date = new Date(year, month - 1, day);
  if (date.getFullYear() !== year || date.getMonth() !== month - 1 || date.getDate() !== day) return null;
  return date;
}

function dateInputValue(date: Date | null) {
  if (!date || Number.isNaN(date.getTime())) return "";
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

function displayDate(value: string) {
  const date = parseDateValue(value);
  if (!date) return "";
  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  return `${day}.${month}.${date.getFullYear()}`;
}

function inclusiveDayCount(dateFrom: Date | null, dateTo: Date | null) {
  if (!dateFrom || !dateTo) return null;
  const fromUtc = Date.UTC(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate());
  const toUtc = Date.UTC(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate());
  if (fromUtc > toUtc) return null;
  return Math.round((toUtc - fromUtc) / 86_400_000) + 1;
}

function rangeDayClassName(day: Date, dateFrom: string, dateTo: string) {
  const value = dateInputValue(day);
  const isStart = Boolean(dateFrom && value === dateFrom);
  const isEnd = Boolean(dateTo && value === dateTo);
  const isInRange = Boolean(dateFrom && dateTo && dateFrom <= dateTo && value >= dateFrom && value <= dateTo);
  return [
    isInRange ? "date-range-picker-day--in-range" : "",
    isStart ? "date-range-picker-day--start" : "",
    isEnd ? "date-range-picker-day--end" : "",
  ].filter(Boolean).join(" ");
}

const RangePickerDay = forwardRef<HTMLButtonElement, PickerDayProps>(function RangePickerDay(props, ref) {
  return (
    <PickerDay {...props} ref={ref}>
      <span className="date-range-picker-day-label">{props.day.getDate()}</span>
    </PickerDay>
  );
});

type DateRangePickerFieldProps = {
  dateFrom: string;
  dateTo: string;
  onChange: (dateFrom: string, dateTo: string) => void;
  label?: ReactNode;
  className?: string;
  disabled?: boolean;
  clearable?: boolean;
  size?: "small" | "medium";
  startAdornment?: ReactNode;
};

export function DateRangePickerField({
  dateFrom,
  dateTo,
  onChange,
  label = "Date range",
  className,
  disabled = false,
  clearable = false,
  size = "medium",
  startAdornment,
}: DateRangePickerFieldProps) {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const [draftDateFrom, setDraftDateFrom] = useState(dateFrom);
  const [draftDateTo, setDraftDateTo] = useState(dateTo);
  const popoverId = useId();
  const fromValue = useMemo(() => parseDateValue(draftDateFrom), [draftDateFrom]);
  const toValue = useMemo(() => parseDateValue(draftDateTo), [draftDateTo]);
  const invalidRange = Boolean(fromValue && toValue && fromValue.getTime() > toValue.getTime());
  const selectedDayCount = inclusiveDayCount(fromValue, toValue);
  const value = dateFrom && dateTo
    ? `${displayDate(dateFrom)} – ${displayDate(dateTo)}`
    : dateFrom
      ? `From ${displayDate(dateFrom)}`
      : dateTo
        ? `To ${displayDate(dateTo)}`
        : "";
  const draftRangeLabel = draftDateFrom && draftDateTo
    ? `${displayDate(draftDateFrom)} – ${displayDate(draftDateTo)}`
    : draftDateFrom
      ? `From ${displayDate(draftDateFrom)}`
      : draftDateTo
        ? `To ${displayDate(draftDateTo)}`
        : "No dates selected";
  const open = Boolean(anchorEl);
  const openPicker = (anchor: HTMLElement) => {
    setDraftDateFrom(dateFrom);
    setDraftDateTo(dateTo);
    setAnchorEl(anchor);
  };
  const closePicker = () => setAnchorEl(null);
  const applyRange = () => {
    if (invalidRange) return;
    onChange(draftDateFrom, draftDateTo);
    closePicker();
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <TextField
        className={`date-range-picker-field${className ? ` ${className}` : ""}`}
        label={label}
        value={value}
        disabled={disabled}
        size={size}
        fullWidth
        onClick={(event) => {
          if (!disabled) openPicker(event.currentTarget);
        }}
        slotProps={{
          input: {
            readOnly: true,
            startAdornment,
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  className="date-range-picker-button"
                  aria-label="Open date range calendar"
                  aria-haspopup="dialog"
                  aria-expanded={open}
                  aria-controls={open ? popoverId : undefined}
                  disabled={disabled}
                  edge="end"
                  onClick={(event) => {
                    event.stopPropagation();
                    if (!disabled) openPicker(event.currentTarget);
                  }}
                >
                  <CalendarTodayOutlinedIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          },
          inputLabel: { shrink: true },
          htmlInput: {
            readOnly: true,
            "aria-haspopup": "dialog",
            "aria-expanded": open,
          },
        }}
      />
      <Popover
        id={popoverId}
        open={open}
        anchorEl={anchorEl}
        onClose={closePicker}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        marginThreshold={8}
        slotProps={{ paper: { className: "date-range-picker-popover" } }}
      >
        <div className="date-range-picker-header">
          <div>
            <strong>
              Select date range
              {selectedDayCount ? ` · ${selectedDayCount} ${selectedDayCount === 1 ? "day" : "days"}` : ""}
            </strong>
            <span className="date-range-picker-selection">{draftRangeLabel}</span>
          </div>
          <IconButton size="small" aria-label="Close date range calendar" onClick={closePicker}>
            <CloseIcon fontSize="small" />
          </IconButton>
        </div>
        <div className="date-range-picker-calendars">
          <section aria-label="From date calendar">
            <div className="date-range-picker-calendar-header">
              <span>From</span>
            </div>
            <DateCalendar
              value={fromValue}
              onChange={(nextDate) => setDraftDateFrom(dateInputValue(nextDate))}
              views={["year", "month", "day"]}
              openTo="day"
              slots={{ day: RangePickerDay }}
              slotProps={{ day: (ownerState) => ({ className: rangeDayClassName(ownerState.day, draftDateFrom, draftDateTo) }) }}
            />
          </section>
          <section aria-label="To date calendar">
            <div className="date-range-picker-calendar-header">
              <span>To</span>
              <Button type="button" size="small" onClick={() => setDraftDateTo(dateInputValue(appToday()))}>
                Today
              </Button>
            </div>
            <DateCalendar
              value={toValue}
              onChange={(nextDate) => setDraftDateTo(dateInputValue(nextDate))}
              views={["year", "month", "day"]}
              openTo="day"
              slots={{ day: RangePickerDay }}
              slotProps={{ day: (ownerState) => ({ className: rangeDayClassName(ownerState.day, draftDateFrom, draftDateTo) }) }}
            />
          </section>
        </div>
        {invalidRange ? (
          <FormHelperText className="date-range-picker-error" error aria-live="polite">
            Start date must be on or before end date.
          </FormHelperText>
        ) : null}
        <div className="date-range-picker-actions">
          {clearable ? (
            <Button
              type="button"
              size="small"
              disabled={!draftDateFrom && !draftDateTo}
              onClick={() => {
                setDraftDateFrom("");
                setDraftDateTo("");
              }}
            >
              Clear
            </Button>
          ) : <span />}
          <Button type="button" size="small" variant="contained" disabled={invalidRange} onClick={applyRange}>
            Apply
          </Button>
        </div>
      </Popover>
    </LocalizationProvider>
  );
}
